import React, { useState } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { ACCOUNT_USER_STATUS } from '@enums';
import { useStore } from '@hooks';
import { Button, Typography } from '@ui';
import { Flex } from 'antd';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { getFullName } from '../../../../utils';
import TeamMember from '../../../TeamMember';

const TransferModal = observer(({ close }) => {
    const store = useStore();
    const [loading, setLoading] = useState(false);
    const { staffAccounts, handleTransferOwnership, currentAccount } = store.accountsStore;
    const [selected, setSelected] = useState({});
    const [success, setSuccess] = useState(false);

    const fullName = getFullName(selected.user?.firstName, selected.user?.lastName);

    const onSubmit = async () => {
        setLoading(true);
        try {
            await handleTransferOwnership(selected, setSuccess);
        } finally {
            setLoading(false);
        }
    };

    const onOk = () => {
        close();
        setSuccess(false);
    };

    if (success) {
        return (
            <React.Fragment>
                <Typography.Text
                    style={BOTTOM_INDENTS.S}
                    data-e2e='team-management_success-transfer-modal_description-1'
                >
                    A confirmation email has been sent to{' '}
                    <a
                        href='mailto:privacy@klickly.com'
                        style={{ color: COLORS.$info, fontWeight: 500 }}
                        target='_blank'
                        rel='noreferrer'
                    >
                        {selected.user?.email}
                    </a>
                    .
                </Typography.Text>
                <Typography.Text
                    style={BOTTOM_INDENTS.XL_PLUS}
                    data-e2e='team-management_success-transfer-modal_description-2'
                >
                    Note: After <span style={{ fontWeight: 500 }}>{fullName}</span> confirms the transfer, your account
                    will be changed to a staff account.
                </Typography.Text>
                <Flex justify='flex-end' align='center' gap={16}>
                    <Button
                        onClick={onOk}
                        type='primary'
                        style={{ width: 160 }}
                        data-e2e='team-management_transfer-modal_ok_btn'
                    >
                        OK
                    </Button>
                </Flex>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Typography.Text style={BOTTOM_INDENTS.S} data-e2e='team-management_transfer-modal_description-1'>
                Select a new owner for the <span style={{ fontWeight: 500 }}>{currentAccount.companyName}</span> store.
            </Typography.Text>
            <Typography.Text style={BOTTOM_INDENTS.M} data-e2e='team-management_transfer-modal_description-2'>
                Note: After the new owner confirms ownership, your account will no longer have owner permissions.
            </Typography.Text>
            <Flex vertical gap={16} style={BOTTOM_INDENTS.XL_PLUS}>
                {staffAccounts
                    .filter(
                        (s) =>
                            s.status === ACCOUNT_USER_STATUS.ACTIVE ||
                            s.status === ACCOUNT_USER_STATUS.TRANSFER_OWNERSHIP
                    )
                    .map((member) => {
                        return (
                            <TeamMember
                                isDisabled={member.status === ACCOUNT_USER_STATUS.TRANSFER_OWNERSHIP}
                                isActive={selected.id === member.id}
                                onClick={setSelected}
                                isSelect
                                key={member.id}
                                member={member}
                            />
                        );
                    })}
            </Flex>
            <Flex justify='flex-end' align='center' gap={16}>
                <Button
                    onClick={close}
                    style={{ width: 160 }}
                    backgroundColor={COLORS.$white}
                    block
                    data-e2e='team-management_transfer-modal_cancel_btn'
                >
                    Cancel
                </Button>
                <Button
                    loading={loading}
                    disabled={_.isEmpty(selected)}
                    onClick={onSubmit}
                    type='primary'
                    style={{ width: 160 }}
                    data-e2e='team-management_transfer-modal_set-as-new-owner_btn'
                >
                    Set as a New Owner
                </Button>
            </Flex>
        </React.Fragment>
    );
});

export default TransferModal;
